/*
 * HALT offers layer: countdown (WCAG 2.2.2 pause control, threshold-only
 * announcements), announcement dismiss. Pure helpers are exposed on
 * window.HaltOffers for unit tests. Everything renders server-side first;
 * this file only enhances.
 */
(() => {
  if (window.HaltOffers && window.HaltOffers.ready) return;

  const doc = document;
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const pad = (n) => String(n).padStart(2, "0");

  function formatRemaining(total, options) {
    const withSeconds = !options || options.withSeconds !== false;
    const s = Math.max(0, Math.floor(total || 0));
    const days = Math.floor(s / 86400);
    const hours = Math.floor((s % 86400) / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    const seconds = s % 60;
    let label;
    if (days >= 1) label = `${days} d ${pad(hours)}:${pad(minutes)}`;
    else if (withSeconds) label = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    else label = `${pad(hours)}:${pad(minutes)}`;
    return { label, days, hours, minutes, seconds };
  }

  function promoState(nowEpoch, endsAt, clockHours, enabled) {
    if (!enabled || !endsAt || endsAt <= 0) return "off";
    if (nowEpoch >= endsAt) return "expired";
    const remaining = endsAt - nowEpoch;
    if (clockHours > 0 && remaining <= clockHours * 3600) return "ending";
    return "active";
  }

  // Most urgent first, so a tick that crosses several thresholds at once
  // announces the one that matters.
  const THRESHOLDS = [
    [0, "expired"],
    [60, "one_minute"],
    [600, "ten_minutes"],
    [3600, "one_hour"],
  ];
  function thresholdKey(prev, remaining) {
    for (const [limit, key] of THRESHOLDS) {
      if (prev > limit && remaining <= limit) return key;
    }
    return null;
  }

  /* ---------------- Countdown ---------------- */
  const timers = new Map(); // element -> interval id

  function readStrings(el) {
    try {
      return JSON.parse(el.getAttribute("data-strings") || "{}");
    } catch (_) {
      return {};
    }
  }

  function initCountdown(el) {
    if (el.dataset.haltReady) return;
    el.dataset.haltReady = "1";
    const endsAt = parseInt(el.getAttribute("data-ends-at"), 10);
    const label = el.querySelector("[data-halt-countdown-label]");
    const live = el.querySelector("[data-halt-countdown-live]");
    const toggle = el.querySelector("[data-halt-countdown-toggle]");
    if (!endsAt || !label) return;
    const strings = readStrings(el);
    let paused = false;
    let prev = Infinity;
    const stepMs = reducedMotion.matches ? 60000 : 1000;

    const tick = () => {
      const remaining = endsAt - Math.floor(Date.now() / 1000);
      // No announcement on the first tick: page load is not a crossing.
      const key = prev === Infinity ? null : thresholdKey(prev, remaining);
      prev = remaining;
      if (!paused) label.textContent = formatRemaining(remaining, { withSeconds: stepMs < 60000 }).label;
      if (key && live && strings[key]) live.textContent = strings[key];
      if (remaining <= 0) {
        clearInterval(timers.get(el));
        timers.delete(el);
        const root = el.closest("[data-halt-promo-root]");
        if (root) root.remove();
      }
    };

    if (toggle) {
      toggle.addEventListener("click", () => {
        paused = !paused;
        toggle.setAttribute("aria-pressed", String(paused));
        toggle.textContent = paused ? strings.resume || "Resume" : strings.pause || "Pause";
      });
    }
    tick();
    timers.set(el, setInterval(tick, stepMs));
  }

  /* ---------------- Dismiss ---------------- */
  function initDismiss(scope) {
    scope.querySelectorAll("[data-halt-dismissable]").forEach((bar) => {
      if (bar.dataset.haltDismissReady) return;
      bar.dataset.haltDismissReady = "1";
      const btn = bar.querySelector("[data-halt-dismiss]");
      const key = `halt-dismissed:${btn ? btn.getAttribute("data-dismiss-key") : ""}`;
      // Never restore a stored dismiss inside the theme editor: the merchant
      // must always see the bar they are configuring.
      const editor = Boolean(window.Shopify && window.Shopify.designMode);
      try {
        if (!editor && sessionStorage.getItem(key) === "1") bar.hidden = true;
      } catch (_) {}
      if (!btn) return;
      btn.addEventListener("click", () => {
        bar.hidden = true;
        try {
          sessionStorage.setItem(key, "1");
        } catch (_) {}
      });
    });
  }

  /* ---------------- Quantity tiers ---------------- */
  function tierFor(qty, tiers) {
    let hit = null;
    for (const t of tiers || []) if (qty >= t.qty && (!hit || t.qty > hit.qty)) hit = t;
    return hit;
  }
  function initTiers(scope) {
    scope.querySelectorAll("[data-halt-tiers]").forEach((table) => {
      if (table.dataset.haltReady) return;
      table.dataset.haltReady = "1";
      let tiers = [];
      try {
        tiers = JSON.parse(table.getAttribute("data-tiers") || "[]");
      } catch (_) {}
      const section = table.closest(".shopify-section") || doc;
      const input = section.querySelector('input[name="quantity"]');
      const paint = () => {
        const qty = parseInt(input && input.value, 10) || 1;
        const hit = tierFor(qty, tiers);
        table.querySelectorAll("[data-tier-qty]").forEach((row) => {
          const rowQty = parseInt(row.getAttribute("data-tier-qty"), 10);
          const current = hit ? rowQty === hit.qty : rowQty === 1;
          row.classList.toggle("is-current", current);
          if (current) row.setAttribute("aria-current", "true");
          else row.removeAttribute("aria-current");
        });
      };
      if (input) {
        input.addEventListener("change", paint);
        input.addEventListener("input", paint);
      }
      paint();
    });
  }

  /* ---------------- Sticky purchase bar (mobile) ---------------- */
  const root = doc.documentElement;
  function stickyShouldShow(formBottom, viewportHeight) {
    return formBottom < 0 && viewportHeight > 0;
  }
  const stickies = new Map(); // bar -> { io, update }
  function initSticky(scope) {
    scope.querySelectorAll("[data-halt-sticky-bar]").forEach((bar) => {
      if (bar.dataset.haltReady) return;
      bar.dataset.haltReady = "1";
      const form = doc.getElementById(bar.getAttribute("data-form"));
      const priceSrc = doc.getElementById(bar.getAttribute("data-price-source"));
      const priceOut = bar.querySelector("[data-halt-sticky-price]");
      const submit = bar.querySelector("[data-halt-sticky-submit]");
      if (!form) return;
      const mq = window.matchMedia("(max-width: 989px)");
      const sync = () => {
        const src = priceSrc && priceSrc.querySelector(".price-item--last");
        if (src && priceOut) priceOut.textContent = src.textContent.trim();
        const mainBtn = form.querySelector('[type="submit"]');
        if (mainBtn && submit) submit.disabled = mainBtn.disabled;
      };
      const update = () => {
        const show = mq.matches && stickyShouldShow(form.getBoundingClientRect().bottom, window.innerHeight);
        bar.hidden = !show;
        bar.setAttribute("aria-hidden", String(!show));
        bar.classList.toggle("is-visible", show);
        root.classList.toggle("halt-sticky-active", show);
      };
      if (submit) submit.addEventListener("click", () => form.requestSubmit());
      const io = "IntersectionObserver" in window ? new IntersectionObserver(update, { threshold: 0 }) : null;
      if (io) io.observe(form);
      window.addEventListener("scroll", update, { passive: true });
      stickies.set(bar, { io, update, sync });
      sync();
      update();
    });
  }
  function syncStickies() {
    stickies.forEach((s) => s.sync());
  }

  /* ---------------- Copy promo code ---------------- */
  doc.addEventListener("click", (event) => {
    const btn = event.target.closest("[data-halt-copy]");
    if (!btn || !navigator.clipboard) return;
    const original = btn.textContent;
    navigator.clipboard
      .writeText(btn.getAttribute("data-halt-copy"))
      .then(() => {
        btn.textContent = btn.getAttribute("data-copied-label") || original;
        setTimeout(() => (btn.textContent = original), 1500);
      })
      .catch(() => {});
  });

  /* ---------------- Lifecycle ---------------- */
  function initScope(scope = doc) {
    scope.querySelectorAll("[data-halt-countdown]").forEach(initCountdown);
    initDismiss(scope);
    initTiers(scope);
    initSticky(scope);
  }
  function disposeScope(scope = doc) {
    timers.forEach((id, el) => {
      if (scope === doc || scope.contains(el)) {
        clearInterval(id);
        timers.delete(el);
        delete el.dataset.haltReady;
      }
    });
    stickies.forEach((s, bar) => {
      if (scope === doc || scope.contains(bar)) {
        if (s.io) s.io.disconnect();
        window.removeEventListener("scroll", s.update);
        stickies.delete(bar);
        root.classList.remove("halt-sticky-active");
      }
    });
  }

  doc.addEventListener("shopify:section:load", (e) => initScope(e.target));
  // Dawn declares PUB_SUB_EVENTS and subscribe with top-level const, so they
  // are globals but not window properties. On variant change Dawn replaces
  // #price-{id}; we swap the tier ladder root from the same fetched document
  // (no extra request, and it stays out of the price live region), then re-init.
  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const html = event && event.data && event.data.html;
      if (html && typeof html.getElementById === "function") {
        doc.querySelectorAll("[data-halt-tiers-root]").forEach((root) => {
          const fresh = html.getElementById(root.id);
          if (fresh) root.innerHTML = fresh.innerHTML;
        });
      }
      window.requestAnimationFrame(() => {
        initScope(doc);
        syncStickies();
      });
    });
  }
  doc.addEventListener("shopify:section:unload", (e) => disposeScope(e.target));
  if (doc.readyState === "loading")
    doc.addEventListener("DOMContentLoaded", () => initScope(doc), { once: true });
  else initScope(doc);

  window.HaltOffers = {
    ready: true,
    formatRemaining,
    promoState,
    thresholdKey,
    tierFor,
    stickyShouldShow,
    initScope,
    disposeScope,
  };
})();
